import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { UseMutationResult, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { isUndefined } from 'lodash'
import { BadgeDto, BadgePOSTorPUTDto } from '../types'
import { getBadges, createBadge, updateBadge, deleteBadge } from '../api'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { ApiResult, usePortalMutation, usePortalQuery } from 'src/Libs/api'

export const badgesKey = (installationId: number | undefined) => ['installation', installationId, 'badges'] as const

export const useGetBadges = (installationId: number | undefined, enabled = true): UseQueryResult<Nullable<BadgeDto[]>, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQuery({
    queryKey: badgesKey(installationId),
    queryFn: () => getBadges(installationId!, authFetch).then(res => res.value),
    enabled: !isUndefined(installationId) && enabled,
  })
}

export const useCreateBadge = (installationId: number): UseMutationResult<void | null, Error, BadgePOSTorPUTDto> => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  return usePortalMutation({
    mutationFn: async (badge: BadgePOSTorPUTDto) => {
      await createBadge(installationId, badge, authFetch)
      await syncInstallation(installationId, authFetch)
      return new ApiResult<void>()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgesKey(installationId) })
    },
  })
}

export const useUpdateBadge = (installationId: number): UseMutationResult<void | null, Error, { badgeId: string; badge: BadgePOSTorPUTDto }> => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  return usePortalMutation({
    mutationFn: async ({ badgeId, badge }: { badgeId: string; badge: BadgePOSTorPUTDto }) => {
      await updateBadge(installationId, badgeId, badge, authFetch)
      await syncInstallation(installationId, authFetch)
      return new ApiResult<void>()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgesKey(installationId) })
    },
  })
}

export const useDeleteBadge = (installationId: number): UseMutationResult<void | null, Error, string> => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  return usePortalMutation({
    mutationFn: async (badgeId: string) => {
      await deleteBadge(installationId, badgeId, authFetch)
      await syncInstallation(installationId, authFetch)
      return new ApiResult<void>()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: badgesKey(installationId) })
    },
  })
}
